import { seq } from './seq';

export interface Success<T> {
  readonly value: T;
  readonly next: string;
}

export type Result<T> = Success<T> | undefined;

export type ParseFunc<T> = (str: string) => Result<T>;
export type Skipper = (str: string) => string;

export interface Parser<T> {
  parse: ParseFunc<T>;
}

export function defaultSkip(str: string) {
  return str;
}

export const integer: Parser<number> = {
  parse(str: string): Result<number> {
    const match = /^\d+/.exec(str);
    if (match === null) {
      return;
    }
    const r = match[0];
    return { value: parseInt(r, 10), next: str.slice(r.length) };
  },
};

export function str(s: string): Parser<string> {
  return {
    parse(str: string): Result<string> {
      if (str.slice(0, s.length) !== s) {
        return;
      }
      return { value: s, next: str.slice(s.length) };
    },
  };
}

export function re(r: RegExp): Parser<RegExpExecArray> {
  return {
    parse(str: string): Result<RegExpExecArray> {
      r.lastIndex = 0;
      const value = r.exec(str);
      if (value !== null && value.index === 0) {
        return { value, next: str.slice(value[0].length) };
      }
    },
  };
}

export function parserFor<T>(parser: Parser<T>, skip?: Skipper): Parser<T>;
export function parserFor(parser: string, skip?: Skipper): Parser<string>;
export function parserFor(parser: any, skip?: Skipper): Parser<any>;
export function parserFor(parser:any, skip?: Skipper): Parser<any> {
  if (typeof parser === 'string') {
    return str(parser);
  }
  if (typeof parser === 'function') {
    return { parse: parser };
  }
  if (parser instanceof RegExp) {
    return re(parser);
  }
  if (parser instanceof Array) {
    return seq(parser, skip);
  }
  return parser;
}

export function parseAll<T>(str: string, parser: Parser<T>, skip?: Skipper): T | undefined;
export function parseAll(str: string, parser: string, skip?: Skipper): string | undefined;
export function parseAll(str: string, parser: any, skip?: Skipper): any;
export function parseAll(str: string, parser: any, skip?: Skipper) {
  const value = parserFor(parser, skip).parse(str);
  if (value === undefined || value.next !== '') {
    return;
  }
  return value.value;
}
